import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { AxiosError } from 'axios'
import { QueryCache, QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/authStore'
import { handleServerError } from '@/utils/handle-server-error'
import { FontProvider } from './context/font-context'
import { ThemeProvider } from './context/theme-context'
import { ServiceHealthProvider } from './context/service-health-context'
import { OfflineBanner } from './components/ui/offline-banner'
import { createIDBPersister } from './pwa/query-persister'
import { onTabSync } from './pwa/tab-sync'
import { cleanExpiredUrls } from './pwa/idb-store'
import { installConsoleCapture } from './lib/consoleCapture'
import { BUILD_ID, dumpCachesAndReload, shouldRunPwa, startUpdateWatcher } from './lib/update-refresh'
import './index.css'

// Capture console + global errors from the very start so a crash report can
// attach the recent log (see GeneralError → Report crash on GitHub).
installConsoleCapture()

// Stale-deploy recovery: when a redeploy changes chunk hashes, a lazy import of
// an old chunk 404s and Vite fires 'vite:preloadError' — without this it bubbles
// to the error boundary ("Oops! Something went wrong"). Reload ONCE (guarded
// against loops) to pull the fresh bundle. Pairs with sw-custom.ts skipWaiting +
// cleanupOutdatedCaches. The guard is cleared after a clean boot so the next
// stale deploy can also recover.
const PRELOAD_RELOAD_KEY = 'vite-preload-reloaded'
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  if (sessionStorage.getItem(PRELOAD_RELOAD_KEY) === '1') return
  sessionStorage.setItem(PRELOAD_RELOAD_KEY, '1')
  window.location.reload()
})
window.addEventListener('load', () => {
  setTimeout(() => sessionStorage.removeItem(PRELOAD_RELOAD_KEY), 5000)
})

// Generated Routes
import { routeTree } from './routeTree.gen'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // eslint-disable-next-line no-console
        if (import.meta.env.DEV) console.log({ failureCount, error })

        if (failureCount >= 0 && import.meta.env.DEV) return false
        if (failureCount > 3 && import.meta.env.PROD) return false

        return !(
          error instanceof AxiosError &&
          [401, 403].includes(error.response?.status ?? 0)
        )
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      staleTime: 10 * 1000, // 10s
      gcTime: 24 * 60 * 60 * 1000, // 24h — keep cached data for persistence
    },
    mutations: {
      onError: (error) => {
        handleServerError(error)

        if (error instanceof AxiosError) {
          if (error.response?.status === 304) {
            toast.error('Content not modified!')
          }
        }
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof AxiosError) {
        // Only act on the FIRST 401 while still authenticated. Without this
        // guard, several concurrent 401s (or a stale token) each re-fire
        // logout()+navigate() mid-render, looping into React #185
        // ("Maximum update depth exceeded"). Once logged out, further 401s
        // are no-ops here and the route guard handles the redirect.
        if (error.response?.status === 401 && useAuthStore.getState().isAuthenticated) {
          toast.error('Session expired!')
          useAuthStore.getState().logout()
          const redirect = `${router.history.location.href}`
          router.navigate({ to: '/sign-in', search: { redirect } })
        }
        if (error.response?.status === 500) {
          toast.error('Internal Server Error!')
          router.navigate({ to: '/500' })
        }
        if (error.response?.status === 403) {
          // router.navigate("/forbidden", { replace: true });
        }
      }
    },
  }),
})

// IndexedDB persister for React Query cache
const persister = createIDBPersister()

// Create a new router instance
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// ─── BroadcastChannel multi-tab sync ────────────────────────────
onTabSync((msg) => {
  switch (msg.type) {
    case 'AUTH_RESET':
      // Cognito-based authStore on develop exposes logout() rather
      // than auth.reset() (the latter was the CF Access cookie shape
      // on main).
      useAuthStore.getState().logout()
      router.navigate({ to: '/sign-in' })
      break
    case 'CACHE_INVALIDATE':
      queryClient.invalidateQueries({ queryKey: msg.queryKey })
      break
    case 'EVENTS_UPDATED':
      // Another tab fetched fresh events — invalidate so we read from IDB
      queryClient.invalidateQueries({ queryKey: ['events'] })
      break
    // SIGNED_URL is handled internally by tab-sync.ts (writes to IDB)
  }
})

// ─── Service Worker registration ────────────────────────────────
// On update: dump every cache layer and reload AUTOMATICALLY (M directive
// 2026-06-05) — never ask the user to refresh. Auth + prefs survive the
// dump; see lib/update-refresh.ts for the keep/purge contract.
function announceUpdateAndReload() {
  toast.info('N.E.S.T. updated — reloading…', { duration: 2000 })
  setTimeout(() => void dumpCachesAndReload(), 1200)
}

// Scope the PWA to the un-gated prod host only. On the CF-Access-gated host
// (dev-nest) the service worker's precache + lazy chunk fetches 302 to the
// Access login, producing a CORS/precache error wall and broken imports once
// the Zero-Trust session lapses. There we register nothing AND tear down any
// SW + caches a prior build left, so returning testers self-heal. (#447)
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  if (shouldRunPwa(window.location.hostname, true)) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/sw.js')
        .then((reg) => {
          // eslint-disable-next-line no-console
          console.log('[SW] Registered:', reg.scope, '| build:', BUILD_ID)
          reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'activated' && navigator.serviceWorker.controller) {
                  announceUpdateAndReload()
                }
              })
            }
          })
        })
        .catch((err) => console.warn('[SW] Registration failed:', err))
    })
  } else {
    // Access-gated / preview host: never run the PWA. Drop any stale SW and
    // its caches so the CF Access precache failures stop and old hashed chunks
    // aren't served. No reload — the unregistered worker stops controlling on
    // the next navigation.
    void (async () => {
      try {
        const regs = await navigator.serviceWorker.getRegistrations()
        await Promise.allSettled(regs.map((r) => r.unregister()))
        if ('caches' in window) {
          const keys = await caches.keys()
          await Promise.allSettled(keys.map((k) => caches.delete(k)))
        }
      } catch (err) {
        console.warn('[SW] PWA teardown on non-PWA host failed:', err)
      }
    })()
  }
}

// Belt-and-braces: poll /version.json (5 min + tab focus) for long-idle tabs
// and SW-less contexts. No-op on the dev server.
startUpdateWatcher({
  onUpdate: () => toast.info('N.E.S.T. updated — reloading…', { duration: 2000 }),
})

// Clean up expired signed URLs on startup
void cleanExpiredUrls()

// Render the app
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <PersistQueryClientProvider client={queryClient} persistOptions={{ persister, maxAge: 24 * 60 * 60 * 1000 }}>
        <ThemeProvider defaultTheme='dark' storageKey='vite-ui-theme'>
          <FontProvider>
            <ServiceHealthProvider>
              <OfflineBanner />
              <RouterProvider router={router} />
            </ServiceHealthProvider>
          </FontProvider>
        </ThemeProvider>
      </PersistQueryClientProvider>
    </StrictMode>
  )
}
